import React, { useState, useEffect, useRef, useCallback } from 'react';
import NarrativeFeed from './NarrativeFeed';
import SearchBox from './SearchBox';
import MobileProfile from './MobileProfile';
import { fetchDonors as fetchSupabaseDonors, supabaseDonorToAppFormat } from '../lib/supabaseDb';
import { authFetch, authPost } from '../lib/authFetch';
import { sanitizeHtml, escapeHtml } from '../lib/sanitize';
import './DonorPanel.css';

function DonorPanel({ isOpen, onClose, donorName, dataMode, conversationHistory, onAddMessage, onSelectDonor, isInline, user, onAction, initialData, groups = [] }) {
  const [narrative, setNarrative] = useState(null);
  const [showSentences, setShowSentences] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [donorDetails, setDonorDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [chatInput, setChatInput] = useState('');
  const [isChatLoading, setIsChatLoading] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [, setSuggestedActions] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [relatedDonors, setRelatedDonors] = useState([]);
  const [isInsightLoading, setIsInsightLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [donorGroupId, setDonorGroupId] = useState(null);
  const [showChatHistory, setShowChatHistory] = useState(false);
  const [insightMessages, setInsightMessages] = useState([]);
  const [timelineEvents, setTimelineEvents] = useState([]);
  const [isTimelineLoading, setIsTimelineLoading] = useState(false);
  const [timelineLoaded, setTimelineLoaded] = useState(false);
  const [forceTimelineRefresh, setForceTimelineRefresh] = useState(false);
  const [, setAnimatingMessageId] = useState(null);
  const [isNewlyCreated, setIsNewlyCreated] = useState(false);
  const [showResearchHint, setShowResearchHint] = useState(false);
  // Mobile swipe views: 0=Chat, 1=Narrative, 2=Timeline
  const [mobileViewIndex, setMobileViewIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [showMobileProfile, setShowMobileProfile] = useState(false);
  const panelRef = useRef(null);
  const contentRef = useRef(null);
  const insightInProgressRef = useRef(false);
  const lastAnimatedContentRef = useRef(null);

  // Detect mobile viewport
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Swipe gesture handling
  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe && mobileViewIndex < 2) {
      setMobileViewIndex(prev => prev + 1);
    } else if (isRightSwipe && mobileViewIndex > 0) {
      setMobileViewIndex(prev => prev - 1);
    }
  };

  // escapeHtml imported from ../lib/sanitize

  // Sanitize text for display
  const highlightText = useCallback((text) => {
    return sanitizeHtml(escapeHtml(text));
  }, []);

  // Fetch AI suggestions for the donor
  const fetchSuggestions = async (name) => {
    try {
      const response = await authPost('/api/donor-suggestions', {
        donorName: name, mode: dataMode
      });

      if (response.ok) {
        const data = await response.json();
        setSuggestedActions(data.suggestedActions || []);
        setRelatedDonors(data.relatedDonors || []);
      }
    } catch (error) {
      console.error('Error fetching suggestions:', error);
    }
  };

  // Load donor data when panel opens (instant, no AI)
  useEffect(() => {
    if (isOpen && donorName) {
      // Reset state when switching donors
      setInsightMessages([]);
      setTimelineEvents([]);
      setTimelineLoaded(false);
      setAnimatingMessageId(null);
      setShowChatHistory(false);
      setMobileViewIndex(0); // Reset to chat view

      console.log('DonorPanel opened for:', donorName);
      console.log('DonorPanel initialData:', initialData);
      console.log('DonorPanel initialData.narrative:', initialData?.narrative);
      // If we have initial data (newly created donor), use it immediately
      if (initialData && initialData.name === donorName) {
        console.log('Using initialData for panel display');
        setIsNewlyCreated(true);
        const narrativeContent = initialData.narrative || '';
        console.log('Setting narrative content to:', narrativeContent);
        setNarrative({
          title: donorName,
          content: narrativeContent,
          meta: initialData.amount
        });
        setDonorDetails({
          name: donorName,
          amount: initialData.amount || 'Unknown',
          nextMeeting: initialData.nextMeeting || 'Not scheduled',
          nextTask: initialData.nextTask || 'No tasks',
          lastContact: initialData.lastContact || 'Unknown'
        });
      } else {
        console.log('Fetching donor data from database');
        // Otherwise fetch from database
        loadDonorDirectly(donorName);
      }
      fetchSuggestions(donorName);
    } else if (!isOpen) {
      // Reset state when panel closes
      setNarrative(null);
      setShowSentences([]);
      setDonorDetails(null);
      setSuggestedActions([]);
      setRelatedDonors([]);
      setInsightMessages([]);
      setTimelineEvents([]);
      setTimelineLoaded(false);
      setIsNewlyCreated(false);
      lastAnimatedContentRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, donorName, initialData]);

  // Update showSentences when narrative changes
  useEffect(() => {
    if (narrative && narrative.content) {
      const content = narrative.content;

      // Skip if content is the same
      if (content === lastAnimatedContentRef.current) {
        return;
      }
      lastAnimatedContentRef.current = content;

      let paragraphs = content.split(/\n\n+|\n/).map(p => p.trim()).filter(p => p);

      if (paragraphs.length === 0 && content.trim()) {
        paragraphs = [content.trim()];
      }

      // Set all paragraphs immediately (word animation happens in NarrativeFeed)
      setShowSentences(paragraphs.map(p => highlightText(p)));
    }
  }, [narrative, highlightText]);

  // Fetch timeline immediately when panel opens (parallel with donor load)
  useEffect(() => {
    if (isOpen && donorName && !timelineLoaded && !isTimelineLoading) {
      const fetchTimelineData = async () => {
        setIsTimelineLoading(true);
        try {
          const response = await authPost('/api/donor-timeline', {
            donorName,
            mode: dataMode,
            forceRefresh: forceTimelineRefresh
          });

          if (response.ok) {
            const data = await response.json();
            setTimelineEvents(data.timeline || []);
            setTimelineLoaded(true);
            setForceTimelineRefresh(false); // Reset after successful fetch
          }
        } catch (error) {
          console.error('Error fetching timeline:', error);
        } finally {
          setIsTimelineLoading(false);
        }
      };
      fetchTimelineData();
    }
  }, [isOpen, donorName, timelineLoaded, isTimelineLoading, dataMode, forceTimelineRefresh]);

  // Load donor data directly from API or Supabase (instant, no AI)
  const loadDonorDirectly = async (name, retryCount = 0) => {
    setShowSentences([]);
    setIsLoading(true);

    try {
      let donor = null;

      // In real mode with logged-in user, fetch from Supabase
      if (dataMode === 'real' && user?.id) {
        const supabaseDonors = await fetchSupabaseDonors(user.id);
        const found = supabaseDonors.find(d => d.name === name);
        if (found) {
          donor = supabaseDonorToAppFormat(found);
        }
      } else {
        // Otherwise use API
        const response = await authFetch(`/api/donors?mode=${dataMode}`);
        if (!response.ok) throw new Error('Server error');
        const data = await response.json();
        donor = data.donors[name];
      }

      if (donor) {
        // Set narrative directly from donor data
        setNarrative({
          title: name,
          content: donor.narrative,
          meta: donor.amount
        });

        // Set donor details
        setDonorDetails({
          id: donor.id,
          name: name,
          amount: donor.amount || 'Unknown',
          nextMeeting: donor.nextMeeting || 'Not scheduled',
          nextTask: donor.nextTask || 'No tasks',
          lastContact: donor.lastContact || 'Unknown'
        });

        // Store groupId for later updates
        setDonorGroupId(donor.groupId || donor.group_id || null);

        // Load insight messages if available (don't animate loaded messages)
        if (donor.insightMessages || donor.insight_messages) {
          const messages = donor.insightMessages || donor.insight_messages || [];
          setInsightMessages(messages);
        }

        // Load timeline events if available (instant load from cache)
        if (donor.timelineEvents || donor.timeline_events) {
          const events = donor.timelineEvents || donor.timeline_events || [];
          if (events.length > 0) {
            setTimelineEvents(events);
            setTimelineLoaded(true);
          }
        }
      } else if (retryCount < 2) {
        // Donor not found - might be newly created, retry after a delay
        console.log(`Donor "${name}" not found, retrying... (attempt ${retryCount + 1})`);
        await new Promise(resolve => setTimeout(resolve, 500));
        return loadDonorDirectly(name, retryCount + 1);
      } else {
        setNarrative({
          title: 'Not Found',
          content: `Could not find "${name}".`
        });
      }
    } catch (error) {
      console.error('Failed to load donor:', error);
      setNarrative({
        title: 'Error',
        content: 'Sorry, I had trouble loading that person. Try again?'
      });
    }

    setIsLoading(false);
  };

  // Fetch via AI (used for chat/search queries)
  const fetchDonorNarrative = async (name) => {
    setShowSentences([]);
    setIsLoading(true);
    setDonorDetails(null);

    try {
      // Fetch narrative
      const response = await authPost('/api/people-search', {
        query: name, mode: dataMode
      });

      if (response.ok) {
        const data = await response.json();
        if (data.title && data.content) {
          setNarrative({
            title: data.title,
            content: data.content,
            meta: 'Donor Profile'
          });

          // Fetch donor details from the donors list
          const donorsResponse = await authFetch(`/api/donors?mode=${dataMode}`);
          if (donorsResponse.ok) {
            const donorsData = await donorsResponse.json();
            const donor = donorsData.donors[data.title];
            if (donor) {
              setDonorDetails({
                name: data.title,
                amount: donor.amount || 'Unknown',
                nextMeeting: donor.nextMeeting || 'Not scheduled',
                nextTask: donor.nextTask || 'No tasks',
                lastContact: donor.lastContact || 'Unknown'
              });
            }
          }
        }
      }
    } catch (error) {
      console.error('Error fetching donor:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch web insight about the donor and save it permanently
  const handleInsightClick = async () => {
    console.log('handleInsightClick called', { donorName, inProgress: insightInProgressRef.current });

    // Use ref for synchronous check to prevent double-execution
    if (!donorName || insightInProgressRef.current) {
      console.log('handleInsightClick early return', { donorName, inProgress: insightInProgressRef.current });
      return;
    }

    // Set ref immediately (synchronous) to block any subsequent calls
    insightInProgressRef.current = true;
    setIsInsightLoading(true);
    console.log('handleInsightClick making API call to /api/donor-insight');

    try {
      const response = await authPost('/api/donor-insight', {
        donorName,
        mode: dataMode
      });
      console.log('handleInsightClick response status:', response.status);

      if (response.ok) {
        const data = await response.json();

        if (data.insight) {
          console.log('Got insight, saving to donor profile');
          // Append the insight to the narrative
          const updatedNarrative = (narrative?.content || '') + '\n\n' + data.insight;

          // Save permanently via donor-update API
          console.log('Calling /api/donor-update with:', { donorName, field: 'narrative', mode: dataMode, groupId: donorGroupId });
          const updateResponse = await authPost('/api/donor-update', {
            donorName,
            field: 'narrative',
            value: updatedNarrative,
            mode: dataMode,
            groupId: donorGroupId
          });
          console.log('donor-update response:', updateResponse.status);

          setNarrative(prev => ({
            ...prev,
            content: updatedNarrative
          }));

          // Reset timeline so it refetches with new narrative content
          setForceTimelineRefresh(true);
          setTimelineLoaded(false);
          setTimelineEvents([]);

          // Trigger list refresh (don't include narrative - it's already saved by /api/donor-update)
          if (onAction) {
            onAction({
              type: 'update_donor',
              data: {
                name: donorName
              }
            });
          }
        } else if (data.message) {
          // No new info found - show temporary indicator
          console.log('Insight:', data.message);
        }
      }
    } catch (error) {
      console.error('Error in handleInsightClick:', error);
      console.error('Error stack:', error.stack);
    } finally {
      console.log('handleInsightClick finished, resetting state');
      insightInProgressRef.current = false;
      setIsInsightLoading(false);
    }
  };


  // Handle deleting the donor
  const handleDeletePerson = () => {
    if (onAction) {
      onAction({ type: 'delete_donor', data: { name: donorName } });
    }
    setShowDeleteConfirm(false);
    if (onClose) {
      onClose();
    }
  };

  // Handle chat input - send message with donor context
  const handleChatSubmit = async () => {
    if (!chatInput.trim() || isChatLoading) return;

    const message = chatInput.trim();
    setChatInput('');
    setIsChatLoading(true);

    // Check if this is a question (Q&A flow) vs adding info (narrative flow)
    const isQuestion = message.endsWith('?') ||
      /^(what|how|why|when|where|who|can|could|would|should|is|are|do|does|tell me|suggest|help)/i.test(message);

    if (isQuestion) {
      // Q&A flow - ask about the donor
      setInsightMessages(prev => [...prev, { role: 'user', content: message }]);
      setShowChatHistory(true); // Show chat history when asking a question

      try {
        const response = await authPost('/api/donor-chat', {
          donorName,
          question: message,
          mode: dataMode,
          groupId: donorGroupId
        });

        if (response.ok) {
          const data = await response.json();
          const answer = data.answer || (data.insightMessages && data.insightMessages[data.insightMessages.length - 1]?.content) || 'No response';
          if (data.insightMessages) {
            setInsightMessages(data.insightMessages);
          } else {
            setInsightMessages(prev => [...prev, { role: 'assistant', content: answer }]);
          }
        } else {
          const errorMsg = 'Sorry, I couldn\'t get an answer. Please try again.';
          setInsightMessages(prev => [...prev, { role: 'assistant', content: errorMsg }]);
        }
      } catch (error) {
        console.error('Error in insight chat:', error);
        const errorMsg = 'An error occurred. Please try again.';
        setInsightMessages(prev => [...prev, { role: 'assistant', content: errorMsg }]);
      } finally {
        setIsChatLoading(false);
      }
      return;
    }

    // Regular narrative flow - add user message to shared history
    if (onAddMessage) {
      onAddMessage({
        role: 'user',
        content: message,
        timestamp: new Date().toISOString()
      });
    }

    try {
      const response = await authPost('/api/people-search', {
        query: message,
        mode: dataMode,
        focusedDonor: donorName
      });

      if (response.ok) {
        const data = await response.json();

        // Add assistant response to shared history
        if (onAddMessage && data.content) {
          onAddMessage({
            role: 'assistant',
            content: data.content,
            action: data.action ? { type: data.action, donorName: donorName } : null,
            timestamp: new Date().toISOString()
          });
        }

        // If note was saved, refresh the narrative and sync to Supabase
        if (data.action === 'save_note') {
          await fetchDonorNarrative(donorName);
          // Reset timeline so it refetches with new narrative content
          setForceTimelineRefresh(true);
          setTimelineLoaded(false);
          setTimelineEvents([]);
          // Trigger onAction for Supabase sync
          if (onAction) {
            onAction({
              type: 'update_donor',
              data: {
                name: donorName,
                updates: { notes: data.note || data.content }
              }
            });
          }
          // Show research hint after first note is saved for newly created person
          if (isNewlyCreated) {
            setIsNewlyCreated(false);
            setShowResearchHint(true);
          }
        } else if (data.action === 'add_donor') {
          // Trigger onAction to create new donor in Supabase
          if (onAction) {
            onAction({
              type: 'add_donor',
              data: {
                name: data.donorName || data.title,
                amount: data.amount || 'Unknown',
                notes: data.notes || data.content || ''
              }
            });
          }
        } else if (data.title && data.content) {
          // Update with new narrative
          setNarrative({
            title: data.title,
            content: data.content,
            meta: 'Donor Profile'
          });
          // Reset timeline so it refetches with new narrative content
          setForceTimelineRefresh(true);
          setTimelineLoaded(false);
          setTimelineEvents([]);
        }

        // Refresh donor details
        const donorsResponse = await authFetch(`/api/donors?mode=${dataMode}`);
        if (donorsResponse.ok) {
          const donorsData = await donorsResponse.json();
          const donor = donorsData.donors[donorName];
          if (donor) {
            setDonorDetails({
              name: donorName,
              amount: donor.amount || 'Unknown',
              nextMeeting: donor.nextMeeting || 'Not scheduled',
              nextTask: donor.nextTask || 'No tasks',
              lastContact: donor.lastContact || 'Unknown'
            });
          }
        }
      }
    } catch (error) {
      console.error('Chat error:', error);
      if (onAddMessage) {
        onAddMessage({
          role: 'assistant',
          content: "Sorry, I couldn't process that. Please try again.",
          timestamp: new Date().toISOString()
        });
      }
    } finally {
      setIsChatLoading(false);
    }
  };

  // Close on escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Close on click outside
  const handleOverlayClick = (e) => {
    if (e.target.classList.contains('donor-panel-overlay')) {
      onClose();
    }
  };

  if (!isOpen) return null;

  // When inline (expanded in main content area), render without overlay wrapper
  if (isInline) {
    return (
      <div className="donor-panel expanded inline" ref={panelRef}>
        <div className="donor-panel-header">
          <button className="donor-panel-close" onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M6 9l6 6 6-6" />
            </svg>
          </button>
          <h2 className="donor-panel-title">{donorName}</h2>
        </div>
        <div className="donor-panel-content">
          {isLoading ? (
            <div className="donor-panel-loading">
              <div className="loading-dots">
                <span></span><span></span><span></span>
              </div>
            </div>
          ) : (
            /* Inline unified view */
            <>
              {/* Top bar with Research, Chat History, and Delete */}
              <div className="donor-panel-top-bar">
                <div className="top-bar-left">
                  <div className="research-button-wrapper">
                    <button
                      className={`research-button ${isInsightLoading ? 'loading' : ''} ${showResearchHint ? 'hint-highlight' : ''}`}
                      onClick={() => { handleInsightClick(); setShowResearchHint(false); }}
                      disabled={isInsightLoading}
                    >
                      {isInsightLoading ? 'Researching...' : (
                        <>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="11" cy="11" r="8" />
                            <path d="M21 21l-4.35-4.35" />
                          </svg>
                          Research
                        </>
                      )}
                    </button>
                    {showResearchHint && (
                      <div className="research-hint-popover">
                        <div className="research-hint-arrow"></div>
                        <div className="research-hint-content">
                          <span className="research-hint-text">Research them online for more context</span>
                          <button className="research-hint-dismiss" onClick={() => setShowResearchHint(false)}>×</button>
                        </div>
                      </div>
                    )}
                  </div>
                  {insightMessages.length > 0 && (
                    <button
                      className={`donor-chat-toggle ${showChatHistory ? 'open' : ''}`}
                      onClick={() => setShowChatHistory(!showChatHistory)}
                      aria-label="Toggle chat history"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                      </svg>
                    </button>
                  )}
                </div>
                <button
                  className="delete-person-x"
                  onClick={() => setShowDeleteConfirm(true)}
                  aria-label="Delete person"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className={`donor-expanded-chat ${isNewlyCreated ? 'highlight-input' : ''}`}>
                <SearchBox
                  placeholder={`Tell me something about ${donorName} or ask a question`}
                  value={chatInput}
                  onChange={(val) => setChatInput(val)}
                  onSearch={handleChatSubmit}
                  loading={isChatLoading}
                  expandable={true}
                  rainbowPlaceholder={true}
                />
                {isNewlyCreated && (
                  <div className="donor-chat-hint-popover">
                    <div className="donor-chat-hint-arrow"></div>
                    <div className="donor-chat-hint-content">
                      <span className="donor-chat-hint-text">Tell me more about your relationship</span>
                      <button className="donor-chat-hint-dismiss" onClick={() => setIsNewlyCreated(false)}>×</button>
                    </div>
                  </div>
                )}
              </div>

              {/* Chat history content - expands below */}
              {showChatHistory && insightMessages.length > 0 && (
                <div className="chat-history-content">
                  {[...insightMessages].reverse().map((msg, idx) => (
                    <div key={idx} className={`insight-message ${msg.role}`}>
                      <span className="insight-message-text">{msg.content}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Narrative with timeline margin */}
              <div className="donor-panel-narrative">
                <div className="narrative-with-timeline">
                  {timelineEvents.length > 0 && (
                    <div className="timeline-margin">
                      {timelineEvents.slice(0, 5).map((event, idx) => (
                        <div key={idx} className={`timeline-margin-item ${event.isPast ? 'past' : 'upcoming'}`}>
                          <span className="timeline-margin-date">
                            {new Date(event.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          </span>
                          <span className="timeline-margin-event">{event.event}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="narrative-content">
                    {narrative && (
                      <NarrativeFeed
                        narrative={narrative}
                        showSentences={showSentences}
                        hideTitle={false}
                      />
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {showDeleteConfirm && (
          <div className="delete-person-overlay" onClick={() => setShowDeleteConfirm(false)}>
            <div className="delete-person-modal" onClick={(e) => e.stopPropagation()}>
              <h3>Delete Person</h3>
              <p>Are you sure you want to delete <strong>{donorName}</strong>? This action cannot be undone.</p>
              <div className="delete-person-actions">
                <button className="delete-person-cancel" onClick={() => setShowDeleteConfirm(false)}>Cancel</button>
                <button className="delete-person-confirm" onClick={handleDeletePerson}>Delete</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="donor-panel-overlay" onClick={handleOverlayClick}>
      <div className="donor-panel" ref={panelRef}>
        <div className="donor-panel-header">
          <button className="donor-panel-close" onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M6 9l6 6 6-6" />
            </svg>
          </button>
          <h2 className="donor-panel-title">{donorName}</h2>
          {isMobile && (
            <button
              className="mobile-profile-btn"
              onClick={() => setShowMobileProfile(true)}
              aria-label="View profile"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                <circle cx="12" cy="7" r="4"/>
              </svg>
            </button>
          )}
        </div>

        <div
          className="donor-panel-content"
          ref={contentRef}
          onTouchStart={isMobile ? onTouchStart : undefined}
          onTouchMove={isMobile ? onTouchMove : undefined}
          onTouchEnd={isMobile ? onTouchEnd : undefined}
        >
          {isLoading ? (
            <div className="donor-panel-loading">
              <div className="loading-dots">
                <span></span><span></span><span></span>
              </div>
            </div>
          ) : isMobile ? (
            /* Mobile: Swipeable views */
            <>
              <div className="mobile-swipe-container">
                {/* View 0: Chat */}
                {mobileViewIndex === 0 && (
                  <div className="mobile-view mobile-chat-view">
                    <div className={`donor-panel-chat ${isNewlyCreated ? 'highlight-input' : ''}`}>
                      <SearchBox
                        placeholder={`Tell me something about ${donorName} or ask a question`}
                        value={chatInput}
                        onChange={(val) => setChatInput(val)}
                        onSearch={handleChatSubmit}
                        loading={isChatLoading}
                        expandable={true}
                        rainbowPlaceholder={true}
                      />
                    </div>
                    {/* Chat history */}
                    <div className="mobile-chat-history">
                      {insightMessages.length > 0 ? (
                        [...insightMessages].reverse().map((msg, idx) => (
                          <div key={idx} className={`insight-message ${msg.role}`}>
                            <span className="insight-message-text">{msg.content}</span>
                          </div>
                        ))
                      ) : (
                        <div className="mobile-chat-empty">
                          <p>Ask a question or add notes about {donorName}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* View 1: Narrative */}
                {mobileViewIndex === 1 && (
                  <div className="mobile-view mobile-narrative-view">
                    <div className="donor-panel-top-bar">
                      <div className="top-bar-left">
                        <div className="research-button-wrapper">
                          <button
                            className={`research-button ${isInsightLoading ? 'loading' : ''}`}
                            onClick={handleInsightClick}
                            disabled={isInsightLoading}
                          >
                            {isInsightLoading ? 'Researching...' : (
                              <>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <circle cx="11" cy="11" r="8" />
                                  <path d="M21 21l-4.35-4.35" />
                                </svg>
                                Research
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                      <button
                        className="delete-person-x"
                        onClick={() => setShowDeleteConfirm(true)}
                        aria-label="Delete person"
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                    <div className="mobile-narrative-content">
                      {narrative && (
                        <NarrativeFeed
                          narrative={narrative}
                          showSentences={showSentences}
                          hideTitle={true}
                        />
                      )}
                    </div>
                  </div>
                )}

                {/* View 2: Timeline */}
                {mobileViewIndex === 2 && (
                  <div className="mobile-view mobile-timeline-view">
                    <h3 className="mobile-timeline-title">Timeline</h3>
                    {isTimelineLoading ? (
                      <div className="donor-panel-loading">
                        <div className="loading-dots">
                          <span></span><span></span><span></span>
                        </div>
                      </div>
                    ) : timelineEvents.length > 0 ? (
                      <div className="mobile-timeline-list">
                        {timelineEvents.map((event, idx) => (
                          <div key={idx} className={`mobile-timeline-item ${event.isPast ? 'past' : 'upcoming'}`}>
                            <span className="mobile-timeline-date">
                              {new Date(event.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                            </span>
                            <span className="mobile-timeline-event">{event.event}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="mobile-timeline-empty">
                        <p>No timeline events yet</p>
                        <p className="mobile-timeline-hint">Add dates to notes to build a timeline</p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Dot indicators */}
              <div className="mobile-view-dots">
                <button
                  className={`mobile-dot ${mobileViewIndex === 0 ? 'active' : ''}`}
                  onClick={() => setMobileViewIndex(0)}
                  aria-label="Chat view"
                />
                <button
                  className={`mobile-dot ${mobileViewIndex === 1 ? 'active' : ''}`}
                  onClick={() => setMobileViewIndex(1)}
                  aria-label="Narrative view"
                />
                <button
                  className={`mobile-dot ${mobileViewIndex === 2 ? 'active' : ''}`}
                  onClick={() => setMobileViewIndex(2)}
                  aria-label="Timeline view"
                />
              </div>
            </>
          ) : (
            /* Desktop: Original layout */
            <>
              {/* Top bar with Research, Chat History, and Delete */}
              <div className="donor-panel-top-bar">
                <div className="top-bar-left">
                  <div className="research-button-wrapper">
                    <button
                      className={`research-button ${isInsightLoading ? 'loading' : ''} ${showResearchHint ? 'hint-highlight' : ''}`}
                      onClick={() => { handleInsightClick(); setShowResearchHint(false); }}
                      disabled={isInsightLoading}
                    >
                      {isInsightLoading ? 'Researching...' : (
                        <>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="11" cy="11" r="8" />
                            <path d="M21 21l-4.35-4.35" />
                          </svg>
                          Research
                        </>
                      )}
                    </button>
                    {showResearchHint && (
                      <div className="research-hint-popover">
                        <div className="research-hint-arrow"></div>
                        <div className="research-hint-content">
                          <span className="research-hint-text">Research them online for more context</span>
                          <button className="research-hint-dismiss" onClick={() => setShowResearchHint(false)}>×</button>
                        </div>
                      </div>
                    )}
                  </div>
                  {insightMessages.length > 0 && (
                    <button
                      className={`donor-chat-toggle ${showChatHistory ? 'open' : ''}`}
                      onClick={() => setShowChatHistory(!showChatHistory)}
                      aria-label="Toggle chat history"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                      </svg>
                    </button>
                  )}
                </div>
                <button
                  className="delete-person-x"
                  onClick={() => setShowDeleteConfirm(true)}
                  aria-label="Delete person"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className={`donor-panel-chat ${isNewlyCreated ? 'highlight-input' : ''}`}>
                <SearchBox
                  placeholder={`Tell me something about ${donorName} or ask a question`}
                  value={chatInput}
                  onChange={(val) => setChatInput(val)}
                  onSearch={handleChatSubmit}
                  loading={isChatLoading}
                  expandable={true}
                  rainbowPlaceholder={true}
                />
                {isNewlyCreated && (
                  <div className="donor-chat-hint-popover">
                    <div className="donor-chat-hint-arrow"></div>
                    <div className="donor-chat-hint-content">
                      <span className="donor-chat-hint-text">Tell me more about your relationship</span>
                      <button className="donor-chat-hint-dismiss" onClick={() => setIsNewlyCreated(false)}>×</button>
                    </div>
                  </div>
                )}
              </div>

              {/* Chat history content - expands below */}
              {showChatHistory && insightMessages.length > 0 && (
                <div className="chat-history-content">
                  {[...insightMessages].reverse().map((msg, idx) => (
                    <div key={idx} className={`insight-message ${msg.role}`}>
                      <span className="insight-message-text">{msg.content}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Main narrative with timeline in margin */}
              <div className="donor-panel-narrative">
                <div className="narrative-with-timeline">
                  {/* Timeline margin */}
                  {timelineEvents.length > 0 && (
                    <div className="timeline-margin">
                      {timelineEvents.slice(0, 5).map((event, idx) => (
                        <div key={idx} className={`timeline-margin-item ${event.isPast ? 'past' : 'upcoming'}`}>
                          <span className="timeline-margin-date">
                            {new Date(event.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          </span>
                          <span className="timeline-margin-event">{event.event}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Narrative content */}
                  <div className="narrative-content">
                    {narrative && (
                      <NarrativeFeed
                        narrative={narrative}
                        showSentences={showSentences}
                        hideTitle={true}
                      />
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {showDeleteConfirm && (
          <div className="delete-person-overlay" onClick={() => setShowDeleteConfirm(false)}>
            <div className="delete-person-modal" onClick={(e) => e.stopPropagation()}>
              <h3>Delete Person</h3>
              <p>Are you sure you want to delete <strong>{donorName}</strong>? This action cannot be undone.</p>
              <div className="delete-person-actions">
                <button className="delete-person-cancel" onClick={() => setShowDeleteConfirm(false)}>Cancel</button>
                <button className="delete-person-confirm" onClick={handleDeletePerson}>Delete</button>
              </div>
            </div>
          </div>
        )}

        {/* New Mobile Profile View */}
        <MobileProfile
          isOpen={showMobileProfile}
          onClose={() => setShowMobileProfile(false)}
          donorName={donorName}
          narrative={narrative}
          timelineEvents={timelineEvents}
          donorDetails={donorDetails}
          onAddNote={() => {
            setShowMobileProfile(false);
          }}
          onSubmitNote={async (message) => {
            // Use universal-chat-stream which handles save_note action
            try {
              const response = await authPost('/api/universal-chat-stream', {
                message,
                mode: dataMode,
                focusedDonor: donorName,
                groupId: donorGroupId
              });

              if (response.ok) {
                // Stream response - read and parse
                const reader = response.body.getReader();
                const decoder = new TextDecoder();
                let action = null;
                let buffer = '';

                while (true) {
                  const { done, value } = await reader.read();
                  if (done) break;

                  // A single SSE event can be split across two reads by the underlying
                  // TCP stream - buffer any incomplete trailing line instead of dropping it.
                  buffer += decoder.decode(value, { stream: true });
                  const lines = buffer.split('\n');
                  buffer = lines.pop();
                  for (const line of lines) {
                    if (line.startsWith('data: ')) {
                      try {
                        const data = JSON.parse(line.slice(6));
                        if (data.action) action = data.action;
                      } catch (e) { /* not JSON */ }
                    }
                  }
                }

                // Refresh narrative after adding note
                await fetchDonorNarrative(donorName);
                // Reset timeline to refetch
                setForceTimelineRefresh(true);
                setTimelineLoaded(false);
                setTimelineEvents([]);

                // Return captured items for confirmation
                return {
                  captured: [
                    { type: 'logged', text: 'Note saved to profile' },
                    ...(action === 'save_note' ? [{ type: 'logged', text: 'Added to timeline' }] : [])
                  ]
                };
              }
            } catch (error) {
              console.error('Error submitting note:', error);
            }
            return { captured: [{ type: 'logged', text: 'Note saved' }] };
          }}
        />
      </div>
    </div>
  );
}

export default DonorPanel;
